import { FC } from 'react';
import { Pax } from 'ts/flight/interfaces/FlightBookResponse';
import { useTranslation } from 'react-i18next';
import { getGenderString } from 'constants/flight';
import { FlightDetails } from 'ts/flight/interfaces/FlightDetailsResponse';
import { BookOptionsCatalogue } from 'ts/flight/classes/BookOptionsCatalogue';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';

interface SuccessPassengerDetailsProps {
  paxes: Pax[];
  baggageFlights?: FlightDetails[];
}

const SuccessPassengerDetails: FC<SuccessPassengerDetailsProps> = ({ paxes, baggageFlights }) => {
  const [t] = useTranslation();

  const baggageOutbound = new BookOptionsCatalogue(t, baggageFlights);

  const getBaggages = (pax: Pax) => {
    return pax.baggageOutbound.split(',').reduce((acc, baggage) => {
      const option = [
        ...baggageOutbound.outboundBaggage.options,
        ...baggageOutbound.outboundMeal.options,
      ].find((opt) => opt.value === baggage);
      if (option) {
        return `${acc}<div class="mb-5">${option.label}</div>`;
      } else {
        return acc;
      }
    }, '');
  };

  return (
    <div className="default-shadow details-summary  mb-30">
      <div className="title-underline text-lg mb-20">{t('passenger.details')}</div>
      {paxes.map((pax, index) => (
        <div className="form-wrapper row mb-15" key={index}>
          <div className="col-12 text-bold mb-10">
            {t('passenger.passenger')} {index + 1}
          </div>
          <div className="col-md-3 col-6 mb-10">
            <div className="mb-5">{t('passenger.gender')}</div>
            <div className="text-contrast">{getGenderString(pax.gender)}</div>
          </div>
          {!!pax.firstName && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('passenger.fn')}</div>
              <div className="text-contrast">{pax.firstName}</div>
            </div>
          )}
          {!!pax.lastName && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('passenger.ln')}</div>
              <div className="text-contrast">{pax.lastName}</div>
            </div>
          )}
          {!!pax.dob && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('passenger.dob')}</div>
              <div className="text-contrast">{dateFormat(pax.dob, 'dd-MMM-yyyy')}</div>
            </div>
          )}
          {!!pax.email && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('passenger.email')}</div>
              <div className="text-contrast">{pax.email}</div>
            </div>
          )}
          {!!pax.phone && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('passenger.phone')}</div>
              <div className="text-contrast">{pax.phone}</div>
            </div>
          )}
          {!!pax.flightFF_AirlineCode && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('flight.frequent_flyer_airline')}</div>
              <div className="text-contrast">{pax.flightFF_AirlineCode}</div>
            </div>
          )}
          {!!pax.flightFF_Number && (
            <div className="col-md-3 col-6 mb-10">
              <div className="mb-5">{t('flight.frequent_flyer_number')}</div>
              <div className="text-contrast">{pax.flightFF_Number}</div>
            </div>
          )}
          {!!pax.baggageOutbound && (
            <div className="col-12">
              <div className="mb-5">{t('passenger.optional_services')}</div>
              <div
                className="text-contrast"
                dangerouslySetInnerHTML={{ __html: getBaggages(pax) }}
              ></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
export default withErrorBoundary(SuccessPassengerDetails, 'SuccessPassengerDetails');
